import { useMemo } from 'react';
import { useCustomerStore } from '../../stores/customerStore';
import type { CustomerContact, PortalModuleId } from '../../lib/api.customer';

/**
 * Solange das Portal noch nicht überall fertig ist, wird je Kunde freigeschaltet,
 * was er sehen soll (Admin → Kundenportal → Bereiche). Der Rest bleibt sichtbar,
 * aber als „Demnächst" gekennzeichnet — ein Bereich, der einfach fehlt, wirkt wie
 * ein Fehler; einer, der angekündigt ist, wirkt wie ein Plan.
 *
 * Zusätzlich lässt sich ein Bereich je Person abschalten (ServiceHub → „Zugriff im Portal").
 * Das steht als „Kein Zugriff" da: Der Bereich kommt nicht irgendwann, er ist für diese Person zu.
 */
const FALLBACK_MODULES: PortalModuleId[] = ['contracts'];

type AreaPerson = Pick<CustomerContact, 'permissions' | 'ticketViewMode'>;

const can = (permission: string) => (person: AreaPerson) =>
  person.permissions?.includes(permission) ?? false;

/**
 * Einlass-Rechte je Bereich — Spiegel von AREA_PERMISSIONS in backend/src/lib/customerAreaAccess.ts.
 * Laufen die auseinander, zeigt die Navigation einen offenen Bereich, dessen Seite mit 403 antwortet.
 */
const AREA_ENTRY: Partial<Record<PortalModuleId, (person: AreaPerson) => boolean>> = {
  requests: can('ticket:create'),
  shop: can('ticket:create'),
  submissions: (p) => p.ticketViewMode === 'ALL' || can('ticket:view_all')(p),
  reports: (p) => p.ticketViewMode === 'ALL' || can('ticket:view_all')(p),
  contracts: can('deadline:view'),
  audit: can('admin:view_audit_logs'),
};

export type PortalAreaState = 'open' | 'no-access' | 'coming-soon';

/** Wie steht ein Bereich für diese Sitzung da? Ohne Hook, damit die Navigation alle auf einmal fragen kann. */
export function portalAreaState(
  module: PortalModuleId,
  contact: CustomerContact | null | undefined
): PortalAreaState {
  const open = contact?.portalModules ?? FALLBACK_MODULES;
  // Alte Sitzung ohne das Feld: bis zum nächsten /me gilt die Freigabe der Person als die der Firma.
  const company = contact?.companyModules ?? open;
  if (!company.includes(module)) return 'coming-soon';
  if (!open.includes(module)) return 'no-access';
  const entry = AREA_ENTRY[module];
  return !entry || (contact && entry(contact)) ? 'open' : 'no-access';
}

export function usePortalAreaState(module: PortalModuleId): PortalAreaState {
  const contact = useCustomerStore((s) => s.contact);
  return portalAreaState(module, contact);
}

/** Kommt diese Person in den Bereich? Z. B. für Links aus einem Bereich in einen anderen. */
export function usePortalModuleEnabled(module: PortalModuleId): boolean {
  return usePortalAreaState(module) === 'open';
}

/** Bereiche, in die diese Person tatsächlich hineinkommt: Firma, persönliche Freigabe, Einlass-Recht. */
export function usePortalModules(): PortalModuleId[] {
  const contact = useCustomerStore((s) => s.contact);
  return useMemo(
    () => (contact?.portalModules ?? FALLBACK_MODULES).filter((id) => portalAreaState(id, contact) === 'open'),
    [contact]
  );
}

/** Recht dieses Ansprechpartners (serverseitig aufgelöst, inkl. Pakete). */
export function useCustomerPermission(permission: string): boolean {
  const permissions = useCustomerStore((s) => s.contact?.permissions);
  return permissions?.includes(permission) ?? false;
}
